/* eslint-disable max-lines */
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { JsonRpcSigner } from '@ethersproject/providers';
import { Redirect, Router, Link as RouterLink } from '@reach/router';
import { useCall, useEthers } from '@usedapp/core';
import { Contract, utils } from 'ethers';

import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import LoaderClaim from '@bitcoin-portal/verse-web-components/dist/LoaderClaim';
import LoaderFarm from '@bitcoin-portal/verse-web-components/dist/LoaderFarm';

import { FarmTransaction } from '@components/FarmTransaction';
import { TxError } from '@components/TxError';
import { TxPending } from '@components/TxPending';
import { TxSuccess } from '@components/TxSuccess';

import {
  DEFAULT_BIGNUM,
  DEFAULT_CHAIN,
  DEFAULT_DECIMALS,
  EMPTY,
  INACTIVE_USDT_WZANO_FARM,
  PRECISION,
  TX_STATUS,
} from '@context/constants';
import { SignerInstanceHandler } from '@context/handlers';
import { useFarms } from '@context/providers/FarmsProvider';
import { useDispatch, useTrackedState } from '@context/store';

import { formatValue } from '@helpers/formatValue';
import {
  StakingInterface,
  chainDetails,
  findProvider,
  supportedChain,
} from '@helpers/index';

import {
  addFarmDeposit,
  claimFarmRewards,
  withdrawFarm,
} from '@views/Farms/helpers/farmTransactions';
import {
  getClaimFlow,
  getFarmDepositFlow,
  getWithdrawFarmFlow,
} from '@views/Farms/helpers/getTxSteps';

import { useCustomAllowance, useCustomBalance } from '@hooks/core';
import { useErc20Controller } from '@hooks/useErc20Controller';

import { FarmsView } from '../FarmsWidget';
import { Investments } from '../Investments';
import ExpiredFarm from '../Investments/ExpiredFarm';
import { InvestmentDetail } from '../Investments/InvestmentDetail';
import { NoDetails } from '../Investments/NoDetails';

interface FarmsProps {
  view: FarmsView;
  setView: Dispatch<SetStateAction<FarmsView>>;
}

export const FarmsConnectedView: FunctionComponent<FarmsProps> = ({
  view,
  setView,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { provider: exchangeProvider, selectedLiquidityPair } =
    useTrackedState();
  const { selectedFarm, farmBalances, farmEarned } = useFarms();

  const { library, account, chainId } = useEthers();
  // farm properties
  const [amountCoin, setAmountCoin] = useState(0);
  const [earnedRewards, setEarnedRewards] = useState(0);
  const [exitedFarm, setExitedFarm] = useState(false);

  const [selectedChain, setSelectedChain] = useState(DEFAULT_CHAIN);
  const [signerInstance, setSignerInstance] = useState<JsonRpcSigner>();
  const [mode, setMode] = useState<'details' | 'deposit' | 'withdraw'>(
    'details',
  );
  const [lpTokenAmount, setLpTokenAmount] = useState('0');

  // pool flow states
  const [txStatus, setTxStatus] = useState(TX_STATUS.ApproveCoin0);
  const [error, setError] = useState(false);
  const [transactionHash, setTransactionHash] = useState(EMPTY);
  const [errorMessage, setErrorMessage] = useState(EMPTY);

  const provider = findProvider(exchangeProvider);
  const details = chainDetails(provider, selectedChain);

  const tickerA = useMemo(
    () => selectedLiquidityPair.token0?.symbol || '',
    [selectedLiquidityPair],
  );
  const tickerB = useMemo(
    () => selectedLiquidityPair.token1?.symbol || '',
    [selectedLiquidityPair],
  );

  const inactiveFarm = INACTIVE_USDT_WZANO_FARM;

  // TODO: REPLACEEEEEEEEEE
  const lpTokenBalance = useCustomBalance(
    selectedLiquidityPair?.id,
    account || '',
    details.chainId,
  );

  const readableLPTokenBalance = parseFloat(
    formatValue(lpTokenBalance, DEFAULT_DECIMALS),
  );

  const stakedBalance = parseFloat(
    formatValue(farmBalances[selectedFarm.contractAddress], DEFAULT_DECIMALS),
  );

  const stakedInOldFarm =
    exchangeProvider === 'bitcoincom_eth' &&
    farmBalances[inactiveFarm] &&
    !farmBalances[inactiveFarm].isZero();

  const earnedAmount = parseFloat(
    formatValue(farmEarned[selectedFarm.contractAddress], DEFAULT_DECIMALS),
  );

  // TODO: REPLACEEEEEEEE
  const amountTokenApproved = useCustomAllowance(
    selectedLiquidityPair?.id,
    account || '',
    selectedFarm.contractAddress,
    details.chainId,
  );

  const resetTx = () => {
    setTxStatus(TX_STATUS.ApproveCoin0);
    setError(false);
  };

  const reset = () => {
    resetTx();
    setView('');
  };

  const erc20Controller = useErc20Controller(
    signerInstance,
    selectedLiquidityPair.id,
  );

  const onConnectWallet = () => {
    dispatch({
      type: 'SET_CONNECT_MODAL_OPEN',
      payload: true,
    });
  };

  // --- CONTRACT CALLS ---
  const makeDeposit = async (input: number, decimals: number) => {
    if (!library || !account) {
      return;
    }

    setView('pending');
    setMode('deposit');
    setAmountCoin(input);

    await addFarmDeposit(
      amountTokenApproved,
      decimals,
      selectedFarm.contractAddress,
      input,
      lpTokenBalance,
      selectedLiquidityPair.id,
      readableLPTokenBalance,
      setTxStatus,
      setTransactionHash,
      setError,
      setErrorMessage,
      setView,
      account,
      erc20Controller,
      signerInstance,
    );
  };

  const makeWithdrawal = async (input: number, decimals: number) => {
    if (!library || !account) {
      return;
    }

    setView('pending');
    setMode('withdraw');
    setLpTokenAmount(input.toString());
    setEarnedRewards(earnedAmount);

    await withdrawFarm(
      decimals,
      earnedAmount,
      selectedFarm.contractAddress,
      input,
      stakedBalance,
      setTxStatus,
      setExitedFarm,
      setTransactionHash,
      setError,
      setErrorMessage,
      setView,
      account,
      signerInstance,
    );
  };

  const makeClaim = async () => {
    if (!library || !account) {
      return;
    }

    if (!earnedAmount) return;

    setView('pending');
    setMode('details');
    setEarnedRewards(earnedAmount);

    await claimFarmRewards(
      selectedFarm.contractAddress,
      setTxStatus,
      setTransactionHash,
      setError,
      setErrorMessage,
      setView,
      account,
      signerInstance,
    );
  };

  const periodFinished =
    useCall({
      contract: new Contract(selectedFarm.contractAddress, StakingInterface),
      method: 'periodFinished',
      args: [],
    })?.value?.[0] || DEFAULT_BIGNUM;

  const now = new Date();
  const farmExpiry = new Date(
    parseFloat(utils.formatUnits(periodFinished, 0)) * 1000,
  );

  useEffect(() => {
    if (chainId && supportedChain(chainId)) {
      setSelectedChain(chainId);
    }
  }, [chainId]);

  // --- STEPS FOR EACH FLOW ---
  const depositFlow = useMemo(() => {
    return getFarmDepositFlow(
      formatMessage,
      tickerA,
      tickerB,
      txStatus,
      error,
      errorMessage,
    );
  }, [error, errorMessage, view, mode, txStatus]);

  const withdrawFlow = useMemo(() => {
    return getWithdrawFarmFlow(formatMessage, txStatus, error, errorMessage);
  }, [error, errorMessage, view, mode, earnedRewards, txStatus]);

  const claimFlow = useMemo(() => {
    return getClaimFlow(formatMessage, txStatus, error, errorMessage);
  }, [error, errorMessage, view, earnedRewards, txStatus]);

  const BackToFarmsBtn = (
    <RouterLink to="/farms/" style={{ width: '100%' }}>
      <Button as="div" fullWidth onClick={reset}>
        <FormattedMessage id="farms.backButton" />
      </Button>
    </RouterLink>
  );

  const InvestmentInfo = () => {
    if (!stakedBalance && !readableLPTokenBalance) {
      return <NoDetails path="/:chain/:pair/" />;
    }
    if (farmExpiry < now) {
      return (
        <ExpiredFarm path="/:chain/:pair/" stakedBalance={stakedBalance} />
      );
    }
    return (
      <InvestmentDetail
        path="/:chain/:pair/"
        stakedBalance={stakedBalance}
        readableLPTokenBalance={readableLPTokenBalance}
        makeClaim={makeClaim}
        view={view}
        reset={reset}
        pendingView={
          <TxPending
            title={formatMessage({
              id: 'farms.global.claim.pending',
            })}
            loaderAnimation={
              <LoaderClaim
                pending={txStatus === TX_STATUS.Pending}
                success={txStatus === TX_STATUS.Success}
                error={error}
              />
            }
            steps={claimFlow}
            footnote={formatMessage({
              id: 'farms.global.claim.pendingFootnote',
            })}
          />
        }
        successView={
          <TxSuccess
            title={formatMessage({
              id: 'farms.global.claim.success',
            })}
            loaderAnimation={<LoaderClaim success />}
            subtitle={formatMessage(
              {
                id: 'farms.claim.successSubtitle',
              },
              {
                AMOUNT: earnedRewards.toFixed(PRECISION),
              },
            )}
            transactionHash={transactionHash}
            customButton={BackToFarmsBtn}
          />
        }
        errorView={
          <TxError
            title={formatMessage({
              id: 'farms.global.claim.fail',
            })}
            loaderAnimation={<LoaderClaim error />}
            steps={claimFlow}
            transactionHash={transactionHash}
            reset={reset}
          />
        }
      />
    );
  };

  return (
    <>
      <SignerInstanceHandler setSignerInstance={setSignerInstance} />
      <Router basepath="/farms">
        <Investments
          path="/:chain/"
          setFarm={(farm: IFarmPair) =>
            dispatch({
              type: 'SET_SELECTED_FARM',
              payload: farm.contractAddress,
            })
          }
          onConnectWallet={onConnectWallet}
          stakedInOldFarm={stakedInOldFarm}
        />
        {InvestmentInfo()}
        <FarmTransaction
          path="/:chain/:pair/deposit/"
          title={
            <div style={{ maxWidth: 350 }}>
              <FormattedMessage
                id="farms.deposit.title"
                values={{ PAIR: `${tickerA}/${tickerB}` }}
              />
            </div>
          }
          triggerTransaction={makeDeposit}
          buttonText={formatMessage({
            id: 'farms.global.deposit.confirm',
          })}
          onConnectWallet={onConnectWallet}
          view={view}
          reset={reset}
          pendingView={
            <TxPending
              title={formatMessage({
                id: 'farms.global.deposit.pending',
              })}
              loaderAnimation={
                <LoaderFarm
                  pending={txStatus === TX_STATUS.Pending}
                  success={txStatus === TX_STATUS.Success}
                  error={error}
                />
              }
              steps={depositFlow}
              footnote={formatMessage(
                { id: 'farms.deposit.pendingFootnote' },
                { PAIR: `${tickerA}/${tickerB}` },
              )}
            />
          }
          successView={
            <TxSuccess
              title={formatMessage({
                id: 'farms.global.deposit.success',
              })}
              loaderAnimation={<LoaderFarm success />}
              subtitle={
                <FormattedMessage
                  id="farms.deposit.successSubtitle"
                  values={{
                    AMOUNT: amountCoin,
                    BR: <br />,
                    PAIR: `${tickerA}/${tickerB}`,
                  }}
                />
              }
              description={formatMessage({
                id: 'farms.deposit.successDescription',
              })}
              transactionHash={transactionHash}
              customButton={BackToFarmsBtn}
            />
          }
          errorView={
            <TxError
              title={formatMessage({
                id: 'farms.global.deposit.fail',
              })}
              loaderAnimation={<LoaderFarm error />}
              steps={depositFlow}
              transactionHash={transactionHash}
              reset={reset}
            />
          }
        />
        <FarmTransaction
          path="/:chain/:pair/withdraw/"
          title={formatMessage(
            { id: 'farms.withdraw.title' },
            { PAIR: `${tickerA}/${tickerB}` },
          )}
          subtitle={formatMessage({
            id: 'farms.withdraw.subtitle',
          })}
          triggerTransaction={makeWithdrawal}
          buttonText={formatMessage({
            id: 'farms.global.withdraw.confirm',
          })}
          withdrawMode
          onConnectWallet={onConnectWallet}
          view={view}
          reset={reset}
          pendingView={
            <TxPending
              title={formatMessage({
                id: 'farms.global.withdraw.pending',
              })}
              loaderAnimation={
                <LoaderFarm
                  pending={txStatus === TX_STATUS.Pending}
                  success={txStatus === TX_STATUS.Success}
                  error={error}
                />
              }
              steps={withdrawFlow}
              footnote={formatMessage({
                id: 'farms.withdraw.pendingFootnote',
              })}
            />
          }
          successView={
            <TxSuccess
              title={formatMessage({
                id: 'farms.global.withdraw.success',
              })}
              loaderAnimation={<LoaderFarm success />}
              subtitle={formatMessage(
                {
                  id: 'farms.withdraw.successSubtitle',
                },
                {
                  AMOUNT: lpTokenAmount,
                  PAIR: `${tickerA}/${tickerB}`,
                  REWARDS: exitedFarm
                    ? formatMessage(
                        {
                          id: 'farms.withdraw.rewards',
                        },
                        { AMOUNT: earnedRewards },
                      )
                    : '',
                },
              )}
              transactionHash={transactionHash}
              customButton={BackToFarmsBtn}
            />
          }
          errorView={
            <TxError
              title={formatMessage({
                id: 'farms.global.withdraw.fail',
              })}
              loaderAnimation={<LoaderFarm error />}
              steps={withdrawFlow}
              transactionHash={transactionHash}
              reset={reset}
            />
          }
        />
        <Redirect from="/*" to="/404/" noThrow />
      </Router>
    </>
  );
};

export default FarmsConnectedView;
